import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';
import type { NewHomeInput } from './places.ts';

export interface Whereabouts {
  client_id: string;
  lives_at: string | null;
  placement_kind: string | null;
  placed_on: string | null;
  temporary_stay_id: string | null;
  stay_type: string | null;
  currently_at: string | null;
  stay_reason: string | null;
  away_since: string | null;
  expected_return_on: string | null;
  bed_held: boolean | null;
  days_away: number | null;
}

export interface PlacementKind {
  kind_code: string;
  kind_label: string;
  target_type: 'Facility' | 'Property';
  facility_type: string | null;
  is_community: boolean;
}

export interface PlacementForm {
  whereabouts: Whereabouts | null;
  placement_kinds: PlacementKind[];
  /** This client's own properties, each named by residence_name() (061). */
  properties: { id: string; property_type: string; name: string; street: string | null; city: string | null; state: string | null }[];
  /** The client's name, as a new home's name will begin. */
  home_owner_name: string | null;
  stay_types: string[];
  /** The server's answer, not a tier guess: `social_work` at write or full. */
  may_record: boolean;
}

/**
 * Where the client lives and where they are.
 *
 * Always asked — the client page leads with it, because "they are in hospital"
 * is the first thing anyone needs to know about a client they are opening.
 * The pickers come with it rather than on a second request: the catalogue is
 * small and a form that stalls after the button is pressed reads as broken on
 * the connection these tablets have.
 */
export function usePlacementForm(clientId: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'placement-form'],
    queryFn: () => apiFetch<PlacementForm>(`/clients/${clientId}/placement-form`, getToken),
    ...queryDefaults,
  });
}

/**
 * Every write here changes where the client is, so every one invalidates the
 * same things: the client (its header says where they live), the history and
 * timeline, and the visits board — whose facility cards are grouped by where
 * people are now, and whose quota can change with the residence type.
 */
function usePlacementMutation<TBody>(clientId: string, path: string) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) =>
      apiFetch<{ whereabouts: Whereabouts }>(`/clients/${clientId}/${path}`, getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['visits'] });
    },
  });
}

/** A move goes to a place on file, or to a home recorded with it. Exactly one. */
export interface MoveInput {
  kind_code: string;
  target_id?: string;
  new_home?: NewHomeInput;
  moved_on: string;
  reason?: string;
  notes?: string;
}

export interface StayInput {
  stay_type: string;
  facility_id?: string;
  other_location?: string;
  started_on: string;
  expected_return_on?: string;
  reason?: string;
  notes?: string;
}

export const useRecordMove = (clientId: string) =>
  usePlacementMutation<MoveInput>(clientId, 'move');

export const useRecordStay = (clientId: string) =>
  usePlacementMutation<StayInput>(clientId, 'stay');

/** No outcome: 'Returned' is the only ending chosen here. */
export const useEndStay = (clientId: string) =>
  usePlacementMutation<{ ended_on: string; notes?: string }>(clientId, 'stay/end');

export const useConvertStay = (clientId: string) =>
  usePlacementMutation<MoveInput>(clientId, 'stay/convert');
